import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Button, Container, List, ListItem, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import { useState } from "react";
import ContactUsModal from "../layout/ContactUsModal";
import { designTokens } from "../../theme/designTokens";

const { colors, borderRadius } = designTokens;

const cardData = [
  {
    title: "BASIC",
    price: "$$",
    description: "Ideal plan for personal use or solo enterpreneurs",
    features: [
      { feature: "User Defined Reports", value: "NO" },
      { feature: "Standard Dashboards", value: "YES" },
      { feature: "Approvals", value: "NO" },
      { feature: "Electronic Signatures", value: "NO" },
      { feature: "Mobile App for Executives", value: "NO" },
      { feature: "Obligation Management", value: "NO" },
      { feature: "Clause Management", value: "NO" },
      { feature: "Artificial Intelligence (AI)", value: "NO" },
    ],
  },
  {
    title: "STANDARD",
    price: "$$",
    description: "Designed for small and medium businesses (SMBs)",
    features: [
      { feature: "Base Price", value: "50 USD (Minimum 5 User)" },
      { feature: "User Defined Reports", value: "YES" },
      { feature: "Standard Dashboards", value: "YES" },
      { feature: "Approvals", value: "YES" },
      { feature: "Electronic Signatures", value: "YES" },
      { feature: "Mobile App for Executives", value: "NO" },
      { feature: "Obligation Management", value: "NO" },
      { feature: "Clause Management", value: "NO" },
      { feature: "Artificial Intelligence (AI)", value: "YES" },
    ],
  },
  {
    title: "CUSTOM",
    price: "$$",
    description: "Tailored for large businesses or unique requirements",
    features: [
      { feature: "User Defined Reports", value: "YES" },
      { feature: "Standard Dashboards", value: "YES" },
      { feature: "Approvals", value: "YES" },
      { feature: "Electronic Signatures", value: "YES" },
      { feature: "Mobile App for Executives", value: "YES" },
      { feature: "Obligation Management", value: "YES" },
      { feature: "Clause Management", value: "YES" },
      { feature: "Artificial Intelligence (AI)", value: "YES" },
    ],
  },
];

interface PlansCardProps {
  embedded?: boolean;
}

const PlansCard = ({ embedded = false }: PlansCardProps) => {
  const [showContactModal, setShowContactModal] = useState(false);

  const content = (
    <>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, textAlign: "center", pb: embedded ? 4 : 7, py: embedded ? 2 : { xs: 5 } }}
      >
        Start for Free, Upgrade Anytime!
      </Typography>
      <ContactUsModal show={showContactModal} handleClose={() => setShowContactModal(false)} />
      <Grid container spacing={4} justifyContent="center">
        {cardData.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.card,
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                position: "relative",
              }}
            >
              {card.title === "STANDARD" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    backgroundColor: colors.primary,
                    color: colors.white,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 700,
                    fontSize: designTokens.fontSizes.xs,
                    textTransform: "uppercase",
                  }}
                >
                  Most Popular
                </Box>
              )}
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                  {card.title}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                  {card.price}
                </Typography>
                <Box sx={{ minHeight: "4rem", mb: 2 }}>
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    {card.description}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mb: 3, py: 1, boxShadow: "none" }}
                  onClick={() => setShowContactModal(true)}
                >
                  Contact Us
                </Button>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 700, letterSpacing: 1 }}>
                  FEATURES
                </Typography>
                <List disablePadding sx={{ mt: 1 }}>
                  {card.features.map((feature, idx) => (
                    <ListItem key={idx} sx={{ padding: 0, mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {feature.value === "YES" ? (
                          <CheckCircleIcon sx={{ color: colors.success, fontSize: 18 }} />
                        ) : feature.value === "NO" ? (
                          <CancelIcon sx={{ color: colors.error, fontSize: 18 }} />
                        ) : (
                          <CheckCircleIcon sx={{ color: colors.success, fontSize: 18 }} />
                        )}
                        <Typography variant="body2">
                          {feature.feature}
                          {feature.value !== "YES" && feature.value !== "NO" ? `: ${feature.value}` : ""}
                        </Typography>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  if (embedded) return <Box>{content}</Box>;
  return (
    <Container maxWidth={designTokens.spacing.containerMax} sx={{ mt: 10 }}>
      {content}
    </Container>
  );
};

export default PlansCard;
